import React, { type ReactNode, useMemo, useState } from "react";
import { Alert, FormGroup, Input, Label, Spinner } from "reactstrap";
import Button from "~/components/Button";
import useApi from "~/hooks/use-api";

type Organization = {
  uid: string;
  name: string;
  disabled: string | null;
};

/**
 * Multi select list of enabled orgs, with search and a select all shown button.
 */
export default function OrganizationPicker(props: {
  selected: string[];
  onChange: (selected: string[]) => void;
  label?: string;
  /** Orgs to leave out of the list, e.g. ones already assigned. */
  excluded?: string[];
  /** Orgs that blocked the last assignment, highlighted and linked. */
  conflicted?: string[];
  /** Shown directly under the label, e.g. why the last assignment failed. */
  message?: ReactNode;
}) {
  const {
    selected,
    onChange,
    label = "Orgs",
    excluded = [],
    conflicted = [],
  } = props;
  const [search, setSearch] = useState("");
  const {
    data: organizations,
    isLoading,
    error,
  } = useApi<Organization[]>("/api/organization");

  const filtered = useMemo(() => {
    const available = (organizations ?? []).filter(
      (org) => !excluded.includes(org.uid)
    );
    const query = search.trim().toLowerCase();

    if (!query) return available;

    return available.filter((org) => org.name.toLowerCase().includes(query));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations, search, excluded.join(",")]);

  const toggle = (uid: string) =>
    onChange(
      selected.includes(uid)
        ? selected.filter((selectedUid) => selectedUid !== uid)
        : [...selected, uid]
    );

  const allShownSelected =
    filtered.length > 0 && filtered.every((org) => selected.includes(org.uid));

  const toggleShown = () => {
    const shown = filtered.map((org) => org.uid);

    onChange(
      allShownSelected
        ? selected.filter((uid) => !shown.includes(uid))
        : [...selected, ...shown.filter((uid) => !selected.includes(uid))]
    );
  };

  return (
    <FormGroup>
      <Label>{label}</Label>
      {props.message}
      <div className="text-muted small mb-2">
        {selected.length} org{selected.length === 1 ? "" : "s"} selected
        {selected.length > 0 && (
          <>
            {" - "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onChange([]);
              }}
            >
              clear selection
            </a>
          </>
        )}
      </div>

      <div className="d-flex mb-2">
        <Input
          type="search"
          placeholder="Search orgs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mr-2"
        />
        <Button
          type="button"
          onClick={toggleShown}
          disabled={filtered.length === 0}
          className="text-nowrap"
        >
          {allShownSelected ? "Deselect" : "Select"} all shown (
          {filtered.length})
        </Button>
      </div>

      {isLoading ? (
        <Spinner />
      ) : error ? (
        <Alert color="danger" className="mb-0">
          Failed to load orgs: {error.message}
        </Alert>
      ) : (
        <div
          className="border rounded p-2"
          style={{ maxHeight: "20rem", overflowY: "auto" }}
        >
          {filtered.map((org) => (
            <div
              className={`${
                conflicted.includes(org.uid)
                  ? "form-check alert-danger"
                  : "form-check"
              } px-4 py-1`}
              key={org.uid}
            >
              <Input
                type="checkbox"
                id={`org-${org.uid}`}
                checked={selected.includes(org.uid)}
                onChange={() => toggle(org.uid)}
              />{" "}
              <Label check for={`org-${org.uid}`}>
                {org.name}
              </Label>
            </div>
          ))}

          {filtered.length === 0 && (
            <p className="text-muted mb-0">No orgs match that search.</p>
          )}
        </div>
      )}
    </FormGroup>
  );
}
